import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Popover,
  Badge,
  Divider,
  Chip,
  Stack,
  Avatar,
  Button,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as BellIcon,
  Medication as MedIcon,
  NoteAlt as NoteIcon,
  AccessTime as TimeIcon,
  FiberManualRecord as DotIcon,
  DoneAll as MarkAllIcon,
  NotificationsOff as EmptyIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import notificationService from '../services/notificationService';
import userService from '../services/userService';
import { parseApiDateTime, formatToUIDateTimeLong } from '../utils/dateUtils';

// ─── Types ──────────────────────────────────────────────────────────────
type NotifCategory = 'medication' | 'progress_note' | 'medication_order' | 'resident_status' | 'other';

interface AppNotification {
  id: string;
  category: NotifCategory;
  title: string;
  message: string;
  residentName: string;
  residentId?: string;
  time: string;
  read: boolean;
  urgency: 'high' | 'medium' | 'low';
}

// Route resolver
function resolveRoute(category: NotifCategory, residentId?: string): string {
  if (category === 'medication_order') return '/medication-orders';
  if (category === 'resident_status') return '/clinical-notes';
  
  const base = category === 'medication' ? '/medication-schedule' : '/resident-progress-notes';
  return residentId ? `${base}?residentId=${residentId}` : base;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function timeAgo(dateInput?: string | Date | null): string {
  if (!dateInput) return 'Just now';
  const parsed = dateInput instanceof Date ? dateInput : parseApiDateTime(dateInput);
  if (!parsed) return 'Just now';
  const diffMs = Date.now() - parsed.getTime();
  if (diffMs < 60000) return 'Just now';
  const m = Math.floor(diffMs / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return formatToUIDateTimeLong(parsed);
}

const URGENCY_COLOR: Record<string, string> = {
  high: '#DC2626',
  medium: '#D97706',
  low: '#16A34A',
};

const CATEGORY_CONFIG: Record<NotifCategory, { icon: React.ReactNode; color: string; bg: string }> = {
  medication: { icon: <MedIcon sx={{ fontSize: 16 }} />, color: '#0077B6', bg: '#E0F2FE' },
  medication_order: { icon: <MedIcon sx={{ fontSize: 16 }} />, color: '#0077B6', bg: '#E0F2FE' },
  progress_note: { icon: <NoteIcon sx={{ fontSize: 16 }} />, color: '#7C3AED', bg: '#F5F3FF' },
  resident_status: { icon: <BellIcon sx={{ fontSize: 16 }} />, color: '#16A34A', bg: '#DCFCE7' },
  other: { icon: <BellIcon sx={{ fontSize: 16 }} />, color: '#5C6F85', bg: '#F1F5F9' },
};

// Session cache to prevent re-querying Keycloak user mapping
let cachedDbUserId: string | null = null;

// In-memory cache for unread count to prevent re-fetching on every screen/route change
let cachedUnreadBadgeCount: number | null = null;
let lastUnreadFetchTime: number = 0;
const UNREAD_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

// ─── Component ────────────────────────────────────────────────────────────────
const NotificationDropdown: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [dbUserId, setDbUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [unreadBadgeCount, setUnreadBadgeCount] = useState<number>(() => cachedUnreadBadgeCount ?? 0);

  const resolveUserId = async (): Promise<string | null> => {
    if (dbUserId) return dbUserId;
    if (cachedDbUserId) {
      setDbUserId(cachedDbUserId);
      return cachedDbUserId;
    }
    const sessionKey = user?.id ? `carex_db_user_${user.id}` : null;
    if (sessionKey) {
      const stored = sessionStorage.getItem(sessionKey);
      if (stored) {
        cachedDbUserId = stored;
        setDbUserId(stored);
        return stored;
      }
    }
    if (!user?.id) return null;
    try {
      // userService.getUserById physically hits /v1/users/keycloak/{id} to get the internal DB user
      const dto = await userService.getUserById(user.id);
      const internalId = dto?.id || user.id;
      cachedDbUserId = internalId;
      if (sessionKey) {
        sessionStorage.setItem(sessionKey, internalId);
      }
      setDbUserId(internalId);
      return internalId;
    } catch (e) {
      console.warn('Failed to resolve internal user ID. Falling back to Keycloak ID.');
      return user.id;
    }
  };

  const loadUnreadCount = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && cachedUnreadBadgeCount !== null && (now - lastUnreadFetchTime) < UNREAD_CACHE_TTL_MS) {
      setUnreadBadgeCount(cachedUnreadBadgeCount);
      return;
    }

    try {
      const targetUserId = await resolveUserId();
      if (!targetUserId) return;
      const count = await notificationService.getUnreadNotificationCount(targetUserId);
      cachedUnreadBadgeCount = count;
      lastUnreadFetchTime = Date.now();
      setUnreadBadgeCount(count);
    } catch (e) {
      console.warn('Failed to load initial unread notification count', e);
    }
  }, [user?.id]);

  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(() => loadUnreadCount(true), UNREAD_CACHE_TTL_MS);
    return () => clearInterval(interval);
  }, [loadUnreadCount]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const targetUserId = await resolveUserId();
      if (!targetUserId) {
        setLoading(false);
        return;
      }

      // Fetch all notifications so in-memory tabs (All vs Unread) work instantly without extra network calls
      const resp = await notificationService.getUserNotifications(targetUserId);
        
      console.log('🔔 [DEBUG] Notifications API Response for User:', targetUserId, '\nData:', resp);
      
      // Defensively extract the array if the backend wrapped it (e.g., Spring Boot paginated / HATEOAS response)
      let dataList: any[] = [];
      if (Array.isArray(resp)) {
        dataList = resp;
      } else if (resp && typeof resp === 'object') {
        const arraysInObject = Object.values(resp).filter(Array.isArray);
        dataList = (resp as any).content || (resp as any).data || (resp as any)._embedded?.notifications || (resp as any).items || (arraysInObject.length > 0 ? arraysInObject[0] : []);
      }

      const mapped: AppNotification[] = dataList.map((dto: any) => {
        let residentName = 'Unknown Resident';
        if (dto.metadata) {
          try {
            const meta = typeof dto.metadata === 'string' ? JSON.parse(dto.metadata) : dto.metadata;
            residentName = meta.residentName || meta.patientName || 'Facility Update';
          } catch (e) {
            residentName = 'System Update';
          }
        }
        
        let cat: NotifCategory = 'other';
        const typeMatch = (dto.notificationType || '').toLowerCase();
        const titleMatch = (dto.subject || '').toLowerCase();
        
        if (titleMatch.includes('medication order') || typeMatch.includes('order')) cat = 'medication_order';
        else if (titleMatch.includes('resident status')) cat = 'resident_status';
        else if (typeMatch.includes('medication') || typeMatch.includes('refill')) cat = 'medication';
        else if (typeMatch.includes('note') || typeMatch.includes('progress')) cat = 'progress_note';

        let urgency: 'high' | 'medium' | 'low' = 'medium';
        const priorityMatch = (dto.priority || '').toLowerCase();
        if (priorityMatch === 'high' || priorityMatch === 'urgent') urgency = 'high';
        else if (priorityMatch === 'low') urgency = 'low';

        return {
          id: dto.id || Math.random().toString(),
          category: cat,
          title: dto.subject || 'Notification',
          message: dto.message || '',
          residentName,
          residentId: dto.relatedEntityId,
          time: dto.sentAt || dto.createdAt || dto.updatedAt || new Date().toISOString(),
          read: dto.isRead || false,
          urgency,
        };
      });
      // Sort so newest are first
      mapped.sort((a, b) => {
        const timeA = parseApiDateTime(a.time)?.getTime() || 0;
        const timeB = parseApiDateTime(b.time)?.getTime() || 0;
        return timeB - timeA;
      });
      setNotifications(mapped);
      const unread = mapped.filter((n) => !n.read).length;
      cachedUnreadBadgeCount = unread;
      lastUnreadFetchTime = Date.now();
      setUnreadBadgeCount(unread);
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
    } finally {
      setLoading(false);
    }
  };

  const open = Boolean(anchorEl);
  const unreadCount = notifications.length > 0
    ? notifications.filter((n) => !n.read).length
    : unreadBadgeCount;

  const displayed = notifications.filter((n) =>
    filter === 'all' ? true : !n.read
  );

  const markAllRead = async () => {
    try {
      const targetUserId = await resolveUserId();
      if (!targetUserId) return;
      
      // Hit backend API to mark all as read
      await notificationService.markAllAsRead(targetUserId);
      
      // Update UI optimistically so dots disappear instantly
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      cachedUnreadBadgeCount = 0;
      setUnreadBadgeCount(0);
    } catch (e) {
      console.error('Failed to mark all as read', e);
    }
  };

  const handleNotificationClick = async (n: AppNotification) => {
    try {
      await notificationService.markAsRead(n.id);
      setNotifications((prev) => prev.map((item) => item.id === n.id ? { ...item, read: true } : item));
      cachedUnreadBadgeCount = Math.max(0, (cachedUnreadBadgeCount || 1) - 1);
      setUnreadBadgeCount(cachedUnreadBadgeCount);
    } catch(e) {
      console.error('Failed to mark as read', e);
    }
    
    // Close popover
    setAnchorEl(null);
    // Navigate to the relevant page, passing residentId as state
    const route = resolveRoute(n.category, n.residentId);
    navigate(route, {
      state: {
        residentId: n.residentId,
        residentName: n.residentName,
        fromNotification: true,
      },
    });
  };

  return (
    <>
      {/* Bell Button */}
      <Tooltip title="Notifications" arrow>
        <IconButton
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            fetchNotifications();
          }}
          sx={{
            color: '#5C6F85',
            '&:hover': { backgroundColor: 'rgba(0,119,182,0.08)', color: '#0077B6' },
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={9}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                fontWeight: 700,
                minWidth: 16,
                height: 16,
                px: 0.4,
              },
            }}
          >
            <BellIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Popover Panel */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 380,
              maxWidth: "calc(100vw - 32px)",
              maxHeight: 540,
              borderRadius: '16px',
              boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
              border: '1px solid #E2E8F0',
              mt: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2, borderBottom: '1px solid #E2E8F0', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1A2B45' }}>
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Chip
                  label={`${unreadCount} new`}
                  size="small"
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#FEE2E2', color: '#DC2626' }}
                />
              )}
            </Box>
              <Button
                size="small"
                startIcon={<MarkAllIcon sx={{ fontSize: '14px !important' }} />}
                onClick={markAllRead}
                disabled={unreadCount === 0}
                sx={{
                  fontSize: '0.72rem', textTransform: 'none', fontWeight: 600,
                  color: unreadCount === 0 ? '#94A3B8' : '#0077B6', py: 0, minWidth: 0,
                  '&:hover': { backgroundColor: unreadCount === 0 ? 'transparent' : 'rgba(0,119,182,0.06)' },
                }}
              >
                Mark all read
              </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Typography 
              variant="caption" 
              onClick={() => setFilter('all')}
              sx={{ 
                cursor: 'pointer', fontWeight: filter === 'all' ? 700 : 600, fontSize: '0.75rem',
                color: filter === 'all' ? '#0077B6' : '#64748B', 
                borderBottom: filter === 'all' ? '2px solid #0077B6' : '2px solid transparent',
                pb: 1.5,
                transition: 'all 0.2s',
                '&:hover': { color: '#0077B6' }
              }}
            >
              All
            </Typography>
            <Typography 
              variant="caption" 
              onClick={() => setFilter('unread')}
              sx={{ 
                cursor: 'pointer', fontWeight: filter === 'unread' ? 700 : 600, fontSize: '0.75rem',
                color: filter === 'unread' ? '#0077B6' : '#64748B', 
                borderBottom: filter === 'unread' ? '2px solid #0077B6' : '2px solid transparent',
                pb: 1.5,
                transition: 'all 0.2s',
                '&:hover': { color: '#0077B6' }
              }}
            >
              Unread
            </Typography>
          </Box>
        </Box>

        {/* Notification List */}
        <Box sx={{ overflowY: 'auto', flex: 1, p: 1.5, pb: 2 }}>
          {loading ? (
            <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
              <CircularProgress size={28} sx={{ color: '#0077B6' }} />
              <Typography variant="caption" sx={{ color: '#94A3B8' }}>Loading notifications...</Typography>
            </Box>
          ) : displayed.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <EmptyIcon sx={{ fontSize: 48, color: '#E2E8F0', mb: 2 }} />
              <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 500 }}>You're all caught up!</Typography>
            </Box>
          ) : (
            displayed.map((n, idx) => {
              const cat = CATEGORY_CONFIG[n.category];
              return (
                <Box
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  sx={{
                    px: 2, 
                    py: 1.75,
                    mb: 1,
                    borderRadius: '12px',
                    bgcolor: n.read ? '#FFFFFF' : '#F8FAFC',
                    border: '1px solid',
                    borderColor: n.read ? '#F1F5F9' : '#E2E8F0',
                    boxShadow: n.read ? 'none' : '0 2px 8px rgba(0,0,0,0.02)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#F1F5F9', borderColor: '#E2E8F0' },
                    display: 'flex',
                    gap: 1.75,
                    alignItems: 'flex-start',
                    position: 'relative',
                  }}
                >
                  {/* Unread indicator bar */}
                  {!n.read && (
                    <Box sx={{
                      position: 'absolute', left: 0, top: 12, bottom: 12, width: 3, 
                      borderRadius: '0 4px 4px 0', bgcolor: '#0077B6',
                    }} />
                  )}

                  {/* Category Icon */}
                  <Avatar
                    sx={{
                      width: 36, height: 36, bgcolor: cat.bg, color: cat.color,
                      flexShrink: 0, mt: 0.25, border: '2px solid #FFFFFF'
                    }}
                  >
                    {cat.icon}
                  </Avatar>

                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5, gap: 1.5 }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 700, color: n.read ? '#334155' : '#0F172A', fontSize: '0.8rem',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 
                      }}>
                        {n.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, mt: '2px' }}>
                        <Tooltip title={formatToUIDateTimeLong(n.time)} arrow placement="top">
                          <Typography variant="caption" sx={{ color: n.urgency === 'high' ? '#DC2626' : '#94A3B8', fontSize: '0.65rem', whiteSpace: 'nowrap', fontWeight: 600 }}>
                            {timeAgo(n.time)}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Tooltip title={n.message} arrow placement="bottom-start" slotProps={{ tooltip: { sx: { maxWidth: 300, fontSize: '0.75rem', p: 1, bgcolor: '#1A2B45' } } }}>
                      <Typography variant="caption" sx={{ 
                        color: '#64748B', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', 
                        overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.5, fontSize: '0.75rem' 
                      }}>
                        {n.message}
                      </Typography>
                    </Tooltip>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                      <TimeIcon sx={{ fontSize: 13, color: '#94A3B8' }} />
                      <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.68rem', fontWeight: 500 }}>
                        {formatToUIDateTimeLong(n.time)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 2.5, py: 1.5,
            borderTop: '1px solid #E2E8F0',
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
            bgcolor: '#F8FAFC', flexShrink: 0,
          }}
        >
          <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.65rem', fontWeight: 600 }}>
            {notifications.filter((n) => n.read).length} OF {notifications.length} READ

          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationDropdown;
